import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { sdk } from "./sdk"

/**
 * Shopify discounts on Medusa promotions (+ one campaign per discount for
 * start/end dates). Verified against checkout in e2e/discounts.e2e.ts.
 *   amount_off_products → standard, target items (all / products / collections)
 *   amount_off_order    → standard, target order
 *   free_shipping       → standard, 100% off shipping methods
 *   buy_x_get_y         → buyget
 * Minimum purchase: rule item_total ≥ amount, or items.quantity ≥ n.
 */
export type DiscountKind = "amount_off_products" | "amount_off_order" | "free_shipping" | "buy_x_get_y"
export const KIND_LABELS: Record<DiscountKind, string> = {
  amount_off_products: "Amount off products",
  amount_off_order: "Amount off order",
  free_shipping: "Free shipping",
  buy_x_get_y: "Buy X get Y",
}

export type DiscountDraft = {
  kind: DiscountKind
  method: "code" | "automatic"
  code: string
  value_type: "percentage" | "fixed"
  value: string
  applies_to: "all" | "products" | "collections"
  product_ids: string[]
  collection_ids: string[]
  min_type: "none" | "amount" | "quantity"
  min_value: string
  // buy X get Y
  buy_quantity: string
  buy_product_ids: string[]
  get_quantity: string
  get_product_ids: string[]
  get_value_type: "free" | "percentage"
  get_value: string
  // limits + dates
  limit_total: boolean
  limit: string
  once_per_customer: boolean
  starts_at: string
  has_end: boolean
  ends_at: string
  active: boolean
  /** One-time purchases, subscriptions, or both (subscription lines carry metadata.terify_subscription) */
  purchase_type: "both" | "one_time" | "subscription"
  /** all customers, or only these emails */
  eligibility: "all" | "customers"
  customer_emails: string[]
  /** Which other discount classes this one may stack with */
  combines: { product: boolean; order: boolean; shipping: boolean }
}

/** Subscription lines have this path; one-time lines don't. Promotion rules read it with eq/ne. */
const SUB_ATTR = "items.metadata.terify_subscription.interval_unit"
const SUB_UNITS = ["day", "week", "month"]
export const DISCOUNT_CLASS: Record<DiscountKind, "product" | "order" | "shipping"> = {
  amount_off_products: "product",
  amount_off_order: "order",
  free_shipping: "shipping",
  buy_x_get_y: "product",
}

const local = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16)

export function emptyDiscount(kind: DiscountKind): DiscountDraft {
  return {
    kind,
    method: "code",
    code: "",
    value_type: "percentage",
    value: "",
    applies_to: "all",
    product_ids: [],
    collection_ids: [],
    min_type: "none",
    min_value: "",
    buy_quantity: "1",
    buy_product_ids: [],
    get_quantity: "1",
    get_product_ids: [],
    get_value_type: "free",
    get_value: "",
    limit_total: false,
    limit: "",
    once_per_customer: false,
    starts_at: local(new Date()),
    has_end: false,
    ends_at: "",
    active: true,
    purchase_type: "both",
    eligibility: "all",
    customer_emails: [],
    combines: { product: false, order: false, shipping: false },
  }
}

export function generateCode(len = 12) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"
  const buf = new Uint32Array(len)
  crypto.getRandomValues(buf)
  return Array.from(buf, (n) => chars[n % chars.length]).join("")
}

const ruleValues = (rules: any[] | undefined, attr: string) => (rules ?? []).find((r) => r.attribute === attr)?.values?.map((v: any) => v.value ?? v) ?? []

export function promotionToDraft(p: any): DiscountDraft {
  const am = p.application_method ?? {}
  const kind: DiscountKind =
    p.type === "buyget"
      ? "buy_x_get_y"
      : am.target_type === "shipping_methods"
        ? "free_shipping"
        : am.target_type === "order" || p.metadata?.terify_kind === "amount_off_order"
          ? "amount_off_order"
          : "amount_off_products"
  const subRule = (am.target_rules ?? []).find((r: any) => r.attribute === SUB_ATTR)
  const emails = ruleValues(p.rules, "email")
  const comb = p.metadata?.combines ?? {}
  const minAmount = (p.rules ?? []).find((r: any) => r.attribute === "item_total")
  const minQty = (p.rules ?? []).find((r: any) => r.attribute === "items.quantity")
  const products = ruleValues(am.target_rules, "items.product.id")
  const collections = ruleValues(am.target_rules, "items.product.collection_id")
  const c = p.campaign
  return {
    ...emptyDiscount(kind),
    method: p.is_automatic ? "automatic" : "code",
    code: p.code ?? "",
    value_type: am.type === "fixed" ? "fixed" : "percentage",
    value: am.value != null ? String(am.value) : "",
    applies_to: collections.length ? "collections" : products.length ? "products" : "all",
    product_ids: products,
    collection_ids: collections,
    min_type: minAmount ? "amount" : minQty ? "quantity" : "none",
    min_value: String((minAmount ?? minQty)?.values?.[0]?.value ?? (minAmount ?? minQty)?.values?.[0] ?? ""),
    buy_quantity: String(am.buy_rules_min_quantity ?? 1),
    buy_product_ids: ruleValues(am.buy_rules, "items.product.id"),
    get_quantity: String(am.apply_to_quantity ?? 1),
    get_product_ids: kind === "buy_x_get_y" ? products : [],
    get_value_type: kind === "buy_x_get_y" && Number(am.value) === 100 && am.type === "percentage" ? "free" : "percentage",
    get_value: kind === "buy_x_get_y" ? String(am.value ?? "") : "",
    limit_total: p.limit != null,
    limit: p.limit != null ? String(p.limit) : "",
    once_per_customer: p.metadata?.once_per_customer === true,
    starts_at: c?.starts_at ? local(new Date(c.starts_at)) : local(new Date(p.created_at ?? Date.now())),
    has_end: !!c?.ends_at,
    ends_at: c?.ends_at ? local(new Date(c.ends_at)) : "",
    active: p.status === "active",
    purchase_type: subRule ? (subRule.operator === "ne" ? "one_time" : "subscription") : "both",
    eligibility: emails.length ? "customers" : "all",
    customer_emails: emails,
    combines: { product: comb.product === true, order: comb.order === true, shipping: comb.shipping === true },
  }
}

export function validateDiscount(d: DiscountDraft): string | null {
  if (d.method === "code" && !/^[A-Za-z0-9_-]{3,40}$/.test(d.code.trim())) return "Discount code must be 3–40 letters, numbers, - or _."
  if (d.method === "automatic" && !d.code.trim()) return "Give the automatic discount a title."
  if (d.kind === "amount_off_products" || d.kind === "amount_off_order") {
    const v = Number(d.value)
    if (!(v > 0)) return "Enter a discount value."
    if (d.value_type === "percentage" && v > 100) return "Percentage can't be more than 100."
  }
  if (d.kind === "amount_off_products" && d.applies_to === "products" && !d.product_ids.length) return "Select at least one product."
  if (d.kind === "amount_off_products" && d.applies_to === "collections" && !d.collection_ids.length) return "Select at least one collection."
  if (d.kind === "buy_x_get_y") {
    if (!(Number(d.buy_quantity) >= 1) || !(Number(d.get_quantity) >= 1)) return "Enter the quantities customers buy and get."
    if (!d.buy_product_ids.length || !d.get_product_ids.length) return "Select the products customers buy and get."
    if (d.get_value_type === "percentage" && !(Number(d.get_value) > 0 && Number(d.get_value) <= 100)) return "Enter a percentage between 1 and 100."
  }
  if (d.min_type !== "none" && !(Number(d.min_value) > 0)) return "Enter the minimum requirement."
  if (d.limit_total && !(Number(d.limit) >= 1 && Number.isInteger(Number(d.limit)))) return "Usage limit must be a whole number."
  if (d.has_end && d.ends_at && new Date(d.ends_at) <= new Date(d.starts_at)) return "End date must be after the start date."
  if (d.eligibility === "customers" && !d.customer_emails.length) return "Add at least one customer, or make the discount available to all customers."
  return null
}

function payload(d: DiscountDraft) {
  const rules: any[] = []
  if (d.min_type === "amount") rules.push({ attribute: "item_total", operator: "gte", values: [String(Number(d.min_value))] })
  if (d.min_type === "quantity") rules.push({ attribute: "items.quantity", operator: "gte", values: [String(Math.floor(Number(d.min_value)))] })
  if (d.eligibility === "customers" && d.customer_emails.length) {
    // Cart email as the shopper typed it is usually lower case; keep both spellings to be safe.
    const vals = [...new Set(d.customer_emails.flatMap((e) => [e.trim(), e.trim().toLowerCase()]).filter(Boolean))]
    rules.push({ attribute: "email", operator: "in", values: vals })
  }
  const purchaseRule = d.purchase_type === "both" || d.kind === "free_shipping" ? [] : [{ attribute: SUB_ATTR, operator: d.purchase_type === "one_time" ? "ne" : "in", values: SUB_UNITS }]
  const base = {
    code: d.method === "code" ? d.code.trim().toUpperCase() : d.code.trim().replace(/\s+/g, "-").toUpperCase(),
    is_automatic: d.method === "automatic",
    status: d.active ? "active" : "inactive",
    limit: d.limit_total ? Math.floor(Number(d.limit)) : null,
    metadata: {
      once_per_customer: d.once_per_customer,
      combines: d.combines,
      // Order discounts limited to a purchase type run as item discounts spread across the cart.
      terify_kind: d.kind === "amount_off_order" && purchaseRule.length ? "amount_off_order" : null,
    },
  }
  let am: any
  let type = "standard"
  switch (d.kind) {
    case "amount_off_order":
      am = purchaseRule.length
        ? { type: d.value_type, target_type: "items", allocation: "across", value: Number(d.value), currency_code: "usd", target_rules: purchaseRule }
        : { type: d.value_type, target_type: "order", allocation: "across", value: Number(d.value), currency_code: "usd" }
      break
    case "free_shipping":
      am = { type: "percentage", target_type: "shipping_methods", allocation: "across", value: 100, currency_code: "usd" }
      break
    case "amount_off_products": {
      const target_rules =
        d.applies_to === "products"
          ? [{ attribute: "items.product.id", operator: "in", values: d.product_ids }]
          : d.applies_to === "collections"
            ? [{ attribute: "items.product.collection_id", operator: "in", values: d.collection_ids }]
            : []
      target_rules.push(...(purchaseRule as any[]))
      am =
        d.value_type === "percentage"
          ? { type: "percentage", target_type: "items", allocation: "each", max_quantity: 10000, value: Number(d.value), currency_code: "usd", target_rules }
          : { type: "fixed", target_type: "items", allocation: "each", max_quantity: 10000, value: Number(d.value), currency_code: "usd", target_rules }
      break
    }
    case "buy_x_get_y":
      type = "buyget"
      am = {
        type: "percentage",
        target_type: "items",
        allocation: "each",
        value: d.get_value_type === "free" ? 100 : Number(d.get_value),
        currency_code: "usd",
        apply_to_quantity: Math.floor(Number(d.get_quantity)),
        max_quantity: Math.floor(Number(d.get_quantity)),
        buy_rules_min_quantity: Math.floor(Number(d.buy_quantity)),
        buy_rules: [{ attribute: "items.product.id", operator: "in", values: d.buy_product_ids }],
        target_rules: [{ attribute: "items.product.id", operator: "in", values: d.get_product_ids }, ...purchaseRule],
      }
      break
  }
  return { ...base, type, rules, application_method: am }
}

const PROMO_FIELDS = "*application_method,*application_method.target_rules,*application_method.target_rules.values,*application_method.buy_rules,*application_method.buy_rules.values,*rules,*rules.values,*campaign,+limit,+used,+metadata"

export function useDiscounts(p: { q?: string; limit: number; offset: number }) {
  return useQuery({
    queryKey: ["discounts", p],
    placeholderData: keepPreviousData,
    queryFn: () => sdk.admin.promotion.list({ q: p.q?.trim() || undefined, limit: p.limit, offset: p.offset, order: "-created_at", fields: PROMO_FIELDS } as any),
  })
}

export function useDiscount(id: string | null) {
  return useQuery({
    queryKey: ["discount", id],
    enabled: !!id,
    queryFn: async () => {
      const { promotion } = await sdk.admin.promotion.retrieve(id!, { fields: PROMO_FIELDS } as any)
      return { promotion, draft: promotionToDraft(promotion) }
    },
  })
}

/** Campaign carries the schedule. One per discount, identified by the promotion code. */
async function upsertCampaign(d: DiscountDraft, existingId?: string | null) {
  const body = {
    name: `Discount ${payload(d).code}`,
    starts_at: new Date(d.starts_at).toISOString(),
    ends_at: d.has_end && d.ends_at ? new Date(d.ends_at).toISOString() : null,
  }
  if (existingId) return (await sdk.admin.campaign.update(existingId, body as any)).campaign.id
  return (await sdk.admin.campaign.create({ ...body, campaign_identifier: `terify-${payload(d).code}-${Date.now().toString(36)}` } as any)).campaign.id
}

export async function saveDiscount(d: DiscountDraft, existing: any | null): Promise<string> {
  const body = payload(d)
  if (!existing) {
    const campaign_id = await upsertCampaign(d)
    const { promotion } = await sdk.admin.promotion.create({ ...body, campaign_id } as any)
    return promotion.id
  }
  const campaign_id = await upsertCampaign(d, existing.campaign_id)
  const { rules, application_method, type: _t, ...rest } = body
  const { target_rules, buy_rules, ...am } = application_method as any
  await sdk.admin.promotion.update(existing.id, { ...rest, campaign_id, application_method: am } as any)
  // Replace rule sets (Medusa edits rules through batch endpoints).
  const replace = async (kind: "rules" | "target-rules" | "buy-rules", current: any[] | undefined, next: any[] | undefined) => {
    const del = (current ?? []).map((r) => r.id)
    if (!del.length && !(next ?? []).length) return
    await sdk.client.fetch(`/admin/promotions/${existing.id}/${kind}/batch`, { method: "POST", body: { create: next ?? [], update: [], delete: del } })
  }
  await replace("rules", existing.rules, rules)
  await replace("target-rules", existing.application_method?.target_rules, target_rules)
  if (d.kind === "buy_x_get_y") await replace("buy-rules", existing.application_method?.buy_rules, buy_rules)
  return existing.id
}

export function useSaveDiscount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { draft: DiscountDraft; existing: any | null }) => saveDiscount(v.draft, v.existing),
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ["discounts"] })
      qc.invalidateQueries({ queryKey: ["discount", id] })
    },
  })
}

export function useDeleteDiscount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (p: any) => {
      await sdk.admin.promotion.delete(p.id)
      if (p.campaign_id) await sdk.admin.campaign.delete(p.campaign_id).catch(() => undefined)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["discounts"] }),
  })
}

/** Shopify's status: Active, Scheduled, Expired (or the deactivated state). */
export function discountStatus(p: any): "active" | "scheduled" | "expired" | "inactive" {
  if (p.status !== "active") return "inactive"
  const now = Date.now()
  if (p.campaign?.starts_at && new Date(p.campaign.starts_at).getTime() > now) return "scheduled"
  if (p.campaign?.ends_at && new Date(p.campaign.ends_at).getTime() < now) return "expired"
  if (p.limit != null && Number(p.used ?? 0) >= Number(p.limit)) return "expired"
  return "active"
}

export function describeDiscount(d: DiscountDraft, names?: { products?: Record<string, string>; collections?: Record<string, string> }) {
  const lines: string[] = []
  const money = (v: string) => `$${Number(v || 0).toFixed(2).replace(/\.00$/, "")}`
  if (d.kind === "free_shipping") lines.push("Free shipping on all products")
  if (d.kind === "amount_off_order") lines.push(`${d.value_type === "percentage" ? `${d.value || 0}%` : money(d.value)} off entire order`)
  if (d.kind === "amount_off_products") {
    const what = d.applies_to === "all" ? "all products" : d.applies_to === "products" ? `${d.product_ids.length} product${d.product_ids.length === 1 ? "" : "s"}` : `${d.collection_ids.length} collection${d.collection_ids.length === 1 ? "" : "s"}`
    lines.push(`${d.value_type === "percentage" ? `${d.value || 0}%` : money(d.value)} off ${what}`)
  }
  if (d.kind === "buy_x_get_y") lines.push(`Buy ${d.buy_quantity} item${d.buy_quantity === "1" ? "" : "s"}, get ${d.get_quantity} item${d.get_quantity === "1" ? "" : "s"} ${d.get_value_type === "free" ? "free" : `at ${d.get_value || 0}% off`}`)
  lines.push(d.min_type === "none" ? "No minimum purchase requirement" : d.min_type === "amount" ? `Minimum purchase of ${money(d.min_value)}` : `Minimum quantity of ${d.min_value || 0} items`)
  if (d.purchase_type !== "both" && d.kind !== "free_shipping") lines.push(d.purchase_type === "one_time" ? "One-time purchases only" : "Subscriptions only")
  lines.push(d.eligibility === "customers" ? `For ${d.customer_emails.length} specific customer${d.customer_emails.length === 1 ? "" : "s"}` : "For all customers")
  const comb = (["product", "order", "shipping"] as const).filter((k) => d.combines[k])
  lines.push(comb.length ? `Combines with ${comb.map((k) => `${k} discounts`).join(", ")}` : "Can't combine with other discounts")
  lines.push(d.limit_total && d.limit ? `Limit of ${d.limit} use${d.limit === "1" ? "" : "s"}${d.once_per_customer ? ", one per customer" : ""}` : d.once_per_customer ? "One use per customer" : "No usage limits")
  const start = d.starts_at ? new Date(d.starts_at) : new Date()
  lines.push(`${start.getTime() > Date.now() ? "Starts" : "Active from"} ${start.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}${d.has_end && d.ends_at ? ` until ${new Date(d.ends_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : ""}`)
  void names
  return lines
}
